import { matchPath } from "react-router-dom";

// Default document titles for staff-workspace routes, applied centrally by
// DocumentTitleManager. Public-portal routes are intentionally absent — they
// set their own localized titles via useDocumentTitle('pageTitles.*').
// Detail routes list a generic singular noun; detail components overwrite it
// with record-specific text (via useLiteralDocumentTitle) once data loads.
// Order matters: most-specific paths first, matched with { end: true }.
export const ROUTE_TITLES = [
  // Inspections (specific subroutes before /inspection/:id)
  { path: "/inspections/:id/unit/:unitId/area/:areaId", title: "Inspection Area" },
  { path: "/inspections/:id/unit/:unitId", title: "Inspection Unit" },
  { path: "/inspections/:id/area/:areaId", title: "Inspection Area" },
  { path: "/inspections/:id/new-unit", title: "New Inspection Unit" },
  { path: "/inspections/:id/conduct", title: "Conduct Inspection" },
  { path: "/inspections/:id/review", title: "Inspection Review" },
  { path: "/inspections/:id/checklist/review", title: "Review & Submit" },
  { path: "/inspections/:id/checklist", title: "Inspection Checklist" },
  { path: "/inspections", title: "Inspections" },
  { path: "/inspection/:id", title: "Inspection" },
  { path: "/my-inspections", title: "My Inspections" },
  { path: "/observations/new", title: "Record an Observation" },
  { path: "/observations", title: "Pending Observations" },

  // Addresses
  { path: "/address/:addressId/unit/:unitId", title: "Unit" },
  { path: "/units/:unitId", title: "Unit" },
  { path: "/situs-addresses/:id", title: "Address" },
  { path: "/address/:id", title: "Address" },
  { path: "/new-address", title: "New Address" },
  { path: "/vacancy-statuses", title: "Vacancy Statuses" },

  // Core staff sections
  { path: "/", title: "Dashboard", authOnly: true },
  { path: "/about", title: "About" },
  { path: "/due-list", title: "Due List" },
  { path: "/sir", title: "SIR Stats" },
  { path: "/contacts/:id", title: "Contact" },
  { path: "/contacts", title: "Contacts" },
  { path: "/violations", title: "Violations" },
  { path: "/violation/:id", title: "Violation" },
  { path: "/cases", title: "Cases" },
  { path: "/case/:id", title: "Case" },
  { path: "/businesses", title: "Businesses" },
  { path: "/business/:id", title: "Business" },
  { path: "/calendar", title: "Calendar" },
  { path: "/map", title: "Map" },
  { path: "/complaints", title: "Complaints" },
  { path: "/complaint/:id", title: "Complaint" },
  { path: "/citations", title: "Citations" },
  { path: "/citation/:id", title: "Citation" },
  { path: "/rooms/:id", title: "Room" },
  { path: "/rooms", title: "Rooms" },
  { path: "/users/:id", title: "User" },
  { path: "/users", title: "Users" },

  // Codes, licenses, permits
  { path: "/codes", title: "Town Code" },
  { path: "/code/:id/edit", title: "Edit Code" },
  { path: "/code/:id", title: "Code" },
  { path: "/licenses", title: "Licenses" },
  { path: "/license/:id", title: "License" },
  { path: "/applications/:id", title: "License Application" },
  { path: "/applications", title: "License Applications" },
  { path: "/permits", title: "Permits" },
  { path: "/permit/:id", title: "Permit" },
  { path: "/permit-applications/:id", title: "Permit Application" },
  { path: "/permit-applications", title: "Permit Applications" },

  // Vehicles
  { path: "/vehicles/:id/inspect", title: "Vehicle Inspection" },
  { path: "/vehicles/:id", title: "Vehicle" },
  { path: "/vehicles", title: "Vehicles" },
  { path: "/vehicle-inspections/:id", title: "Vehicle Inspection" },

  // Misc staff pages
  { path: "/helpful", title: "Helpful Resources" },
  { path: "/reporting", title: "Reporting" },
  { path: "/templates", title: "Templates" },
  { path: "/account", title: "Account" },
  { path: "/tutorial", title: "Tutorial" },
  { path: "/notifications", title: "Notifications" },
  { path: "/photo-bank", title: "Photo Bank" },
  { path: "/photo-review", title: "Photo Review" },
  { path: "/document-upload", title: "Document Upload" },
  { path: "/theme-preview", title: "Theme Preview" },
  { path: "/quiz", title: "Quiz" },
  { path: "/crm/dashboard", title: "EDC Home" },
  { path: "/crm/reports", title: "EDC Reports" },
  { path: "/crm/follow-ups", title: "My Follow-ups" },
  { path: "/crm/spaces", title: "Commercial Spaces" },
  { path: "/vacancies", title: "Vacancies" },
  { path: "/crm/programs", title: "Grants & Programs" },
  { path: "/crm/events", title: "Events & Outreach" },
  { path: "/crm/timelines", title: "Timelines" },
  { path: "/crm/timelines/:id", title: "Timeline" },
  { path: "/crm/corridor-walk", title: "Corridor Walk" },
  { path: "/available-spaces", title: "Available Commercial Spaces" },

  // Admin
  { path: "/admin", title: "Admin Dashboard" },
  { path: "/admin/review", title: "Admin Review" },
  { path: "/admin-chat", title: "Admin Chat" },
  { path: "/admin/code-sync", title: "Code Sync" },
  { path: "/admin/workload", title: "Staff Workload" },
  { path: "/admin/inspector-activity", title: "Inspector Activity" },
  { path: "/admin/audit", title: "Audit Log" },
  { path: "/admin/email-dashboard", title: "Email Dashboard" },
  { path: "/admin/comments/:commentId/edit", title: "Edit Comment" },
  { path: "/admin/quickbooks", title: "QuickBooks" },
  { path: "/admin/public-support/:requestId", title: "Public Support" },
  { path: "/admin/public-support", title: "Public Support" },
  { path: "/admin/document-review", title: "Document Review" },
  { path: "/admin/vehicle-checklist", title: "Vehicle Checklist" },
  { path: "/admin/foia-reports", title: "FOIA Reports" },
  { path: "/admin/quiz-review", title: "Quiz Review" },
];

export const findRouteTitle = (pathname, isAuthenticated) => {
  const entry = ROUTE_TITLES.find(
    ({ path, authOnly }) =>
      (!authOnly || isAuthenticated) && matchPath({ path, end: true }, pathname)
  );
  return entry?.title || "";
};

